import React, { useEffect, useRef, useCallback } from 'react';
import Chart from 'chart.js/auto';
import 'chartjs-adapter-luxon';
import moment from 'moment-timezone';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import { useSelector } from 'react-redux';
import { roundDecimalValue, getStartTime } from '@/utilities/helpers/commonHelper';
import { DateTime } from 'luxon';
import zoomPlugin from 'chartjs-plugin-zoom';

const PulseOximeterChart = ({
  chartData,
  userTimezone,
  is24hoursformat,
  insightStartDateTime,
  insightEndDateTime,
  insightLoading,
  getDateFormatByLocation,
  selectedRange = 'today',
}) => {
  const canvasRef = useRef(null);
  const chartInstanceRef = useRef(null);
  const trans = useSelector(translator);

  const BAR_THICKNESS = 10;
  const MAX_BAR_THICKNESS = 30;

  const getAggregatedData = useCallback(
    (chartDataFn) => {
      const dataArray = Array.isArray(chartDataFn)
        ? chartData.filter((item) => item !== null)
        : chartData
          ? [chartData]
          : [];

      const pulseOximeterData = dataArray
        .map((item) => ({
          timestamp: item?.utc_timestamp_inms
            ? moment.utc(item.utc_timestamp_inms).tz(userTimezone).toDate()
            : null,
          p: item?.data?.pulse || null,
          spo2: item?.data?.oxygen || null,
        }))
        .filter((item) => item.timestamp !== null)
        .sort((a, b) => a.timestamp - b.timestamp);

      let aggregatedData = [];

      aggregatedData = pulseOximeterData.map((h) => {
        return {
          groupTimestamp: h.timestamp,
          actualTimestamp: h.timestamp,
          p: h.p,
          spo2: h.spo2,
        };
      });

      aggregatedData.sort((a, b) => a.groupTimestamp - b.groupTimestamp);

      const latestPerMinute = Object.values(
        aggregatedData.reduce((acc, item) => {
          const ts = new Date(item.actualTimestamp);
          const key = DateTime.fromJSDate(ts)
            .toUTC() // normalize timezone
            .toFormat('yyyy-MM-dd HH:mm');

          // keep latest for that minute
          if (!acc[key] || ts > new Date(acc[key].actualTimestamp)) {
            acc[key] = item;
          }

          return acc;
        }, {})
      );
      return latestPerMinute;
    },
    [chartData, userTimezone]
  );

  useEffect(() => {
    if (insightLoading) {
      return;
    }

    // Display timezone information.
    const tzAbbreviation = moment.tz(new Date(), userTimezone).format('z');
    const tzDisplayText = `${trans('TIMEZONE')}: ${userTimezone}`;

    const timeZonePlugin = {
      id: 'timeZonePlugin',
      afterDatasetsDraw: (chart, args, options) => {
        const { ctx, chartArea } = chart;
        const xAxis = chart.scales.x;
        const xAxisBottom = xAxis ? xAxis.bottom : chartArea.bottom;
        ctx.save();
        ctx.font = options.font || '12px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillStyle = options.color || 'black';
        const text = options.text || '';
        ctx.fillText(text, (chartArea.left + chartArea.right) / 2, xAxisBottom + 20);
        ctx.restore();
      },
    };

    Chart.register(timeZonePlugin);
    Chart.register(zoomPlugin);

    if (chartInstanceRef.current) {
      chartInstanceRef.current.destroy();
      chartInstanceRef.current = null;
    }

    const insightStart = insightStartDateTime
      ? moment.utc(insightStartDateTime).tz(userTimezone)
      : null;
    const insightEnd = insightEndDateTime ? moment.utc(insightEndDateTime).tz(userTimezone) : null;

    const isTodayExplicit = String(selectedRange || '').toLowerCase() === 'today';
    const isTodayHeuristic = !!(
      insightStart &&
      insightEnd &&
      insightStart.isSame(insightEnd, 'day') &&
      Math.abs(insightEnd.diff(insightStart, 'hours', true) - 24) < 1.1
    );
    const isToday = isTodayExplicit || isTodayHeuristic;

    const aggregatedData = getAggregatedData(chartData);

    const labels = aggregatedData.map((row) =>
      moment.tz(row.groupTimestamp, userTimezone).toDate()
    );

    // ---------- Build Chart.js dataset ----------
    const pulseChartData = {
      labels,
      rawTimestamps: aggregatedData.map((row) => row.actualTimestamp),
      datasets: [
        {
          label: 'Pulse (beats/min)',
          data: aggregatedData.map((row) =>
            !isNaN(row.p) ? { x: row.groupTimestamp, y: row.p } : { x: row.groupTimestamp, y: NaN }
          ),
          spanGaps: true,
          showLine: false,
          pointRadius: 5,
          pointHoverRadius: 10,
        },
        {
          label: 'SpO2 (%)',
          data: aggregatedData.map((row) =>
            !isNaN(row.spo2)
              ? { x: row.groupTimestamp, y: row.spo2 }
              : { x: row.groupTimestamp, y: NaN }
          ),
          spanGaps: true,
          showLine: false,
          pointRadius: 5,
          pointHoverRadius: 10,
        },
      ],
    };

    const formatDate = getDateFormatByLocation(userTimezone);
    const tooltipFormatString = is24hoursformat
      ? `${formatDate}, HH:mm:ss`
      : `${formatDate}, hh:mm:ss a`;

    const chartObj = {
      type: 'line',
      data: pulseChartData,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        layout: { padding: { bottom: 42 } },
        interaction: { mode: 'nearest', axis: 'x' },
        animation: { duration: 0 }, // general animation time
        hover: { animationDuration: 0 },
        plugins: {
          title: { display: false },
          tooltip: {
            mode: 'nearest',
            position: 'nearest',
            intersect: false,
            enabled: true,
            axis: 'x',
            filter: (tooltipItem) => {
              const value = tooltipItem.raw?.y ?? tooltipItem.raw;
              return typeof value === 'number' && !isNaN(value);
            },
            callbacks: {
              title: (tooltipItems) => {
                if (!tooltipItems || tooltipItems?.length === 0) return '';
                const idx = tooltipItems[0].dataIndex;
                const rawTimestamp = tooltipItems[0].chart.data.rawTimestamps[idx];
                return moment.tz(rawTimestamp, userTimezone).format(tooltipFormatString);
              },
              label: (tooltipItem) => {
                const value = tooltipItem.raw?.y ?? tooltipItem.raw;
                const datasetLabel = tooltipItem.dataset.label;
                return `${datasetLabel}: ${roundDecimalValue(value)}`;
              },
            },
          },
          datalabels: { display: false },
          timeZonePlugin: {
            text: tzDisplayText,
            font: '14px Arial',
            color: '#666',
          },
          zoom: {
            zoom: {
              wheel: { enabled: false }, // Zoom with mouse wheel - to be enabled later
              pinch: { enabled: false }, // Zoom with pinch gesture - to be enabled later
              mode: 'x', // Zoom direction: 'x', 'y', or 'xy'
            },
            pan: {
              enabled: false, // Enable panning
              mode: 'x', // Pan along X-axis
            },
            limits: {
              x: { min: 'original', max: 'original' },
            },
          },
        },
        scales: {
          x: {
            type: 'time',
            time: {
              unit: isToday ? 'hour' : 'day',
              displayFormats: isToday
                ? { hour: is24hoursformat ? 'HH' : 'ha' } // short hour labels
                : { day: getDateFormatByLocation(userTimezone) },
            },
            min: getStartTime({ selectedRange, userTimezone }),
            max: moment().tz(userTimezone).add(1, 'hour').toDate(),
            ticks: {
              callback: (value) => {
                const date = new Date(value);

                if (isToday) {
                  if (is24hoursformat) {
                    const time = moment.utc(date).tz(userTimezone).format('HH:mm');
                    if (time === '00:00') {
                      return '24:00';
                    }
                    return time;
                  }
                  return moment.utc(date).tz(userTimezone).format('ha');
                }

                return new Intl.DateTimeFormat('en', {
                  month: 'short',
                  day: 'numeric',
                }).format(date); // e.g. "Sep 1"
              },
            },

            grid: { display: true, drawOnChartArea: false },
            border: { color: '#656565', display: true, dash: [5, 5], dashOffset: 1 },
          },
          y: {
            type: 'linear',
            offset: true,
            ticks: { autoSkip: false, precision: false },
            border: {
              color: '#656565',
              display: true,
              dash: [5, 5],
              dashOffset: 1,
            },
            grid: { color: '#989898', lineWidth: 1 },
          },
        },
      },
    };

    chartInstanceRef.current = new Chart(canvasRef.current, chartObj);

    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }
    };
  }, [
    chartData,
    userTimezone,
    is24hoursformat,
    insightStartDateTime,
    insightEndDateTime,
    insightLoading,
    getDateFormatByLocation,
    selectedRange,
  ]);

  return (
    <div className="chart-container">
      <canvas ref={canvasRef} className="chart-canvas" />
    </div>
  );
};

export default PulseOximeterChart;
